import React from "react";
import cloneDeep from "lodash/cloneDeep";

import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Alert from "@cloudscape-design/components/alert";
import SpaceBetween from "@cloudscape-design/components/space-between";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { userStore } from "../stores/access";
import { badgesStore } from "../stores/badges";
import { badgesAddModalStore } from "../stores/badgesAddModal";
import { badgesEditModalStore } from "../stores/badgesEditModal";

const normalizeStatusOption = (statusValue) => {
	const normalizedValue = String(statusValue || "").trim().toLowerCase() === "active" ? "active" : "inactive";
	return {
		label: normalizedValue === "active" ? "Active" : "Inactive",
		value: normalizedValue,
	};
};

class Badges extends React.Component {
	componentDidMount() {
		this.props.Interface.loadBadges();
	}

	getBadgeDateValue(item) {
		return item?.date || item?.updated_at || item?.created_at || "";
	}

	getBadgeDateTimestamp(item) {
		const rawValue = this.getBadgeDateValue(item);
		if (!rawValue) return 0;
		const normalizedValue = typeof rawValue === "string" ? rawValue.replace(" ", "T") : rawValue;
		const timestamp = +new Date(normalizedValue);
		return Number.isFinite(timestamp) ? timestamp : 0;
	}

	formatBadgeDate(item) {
		const rawValue = this.getBadgeDateValue(item);
		if (!rawValue) return "-";
		const normalizedValue = typeof rawValue === "string" ? rawValue.replace(" ", "T") : rawValue;
		const date = new Date(normalizedValue);
		return Number.isFinite(+date) ? date.toLocaleDateString() : "-";
	}

	compareBy(sorting, a, b) {
		const sortingField = sorting?.sortingColumn?.sortingField;
		const isDescending = !!sorting?.isDescending;
		if (!sortingField) return 0;
		const valueA = sortingField === "date" ? this.getBadgeDateTimestamp(a) : a[sortingField];
		const valueB = sortingField === "date" ? this.getBadgeDateTimestamp(b) : b[sortingField];
		if (valueA < valueB) return isDescending ? 1 : -1;
		if (valueA > valueB) return isDescending ? -1 : 1;
		return 0;
	}

	getVisibleColumns() {
		return ["id", "name", "text_color", "background_color", "status", "date"];
	}

	render() {
		const visibleBadges = this.props.badges.data.filter((item) => String(item?.name || "").trim().toLowerCase() !== "rtpup");
		const sortedData = cloneDeep(visibleBadges).sort((a, b) => this.compareBy(this.props.badges.sorting, a, b));
		const pagesCount = Math.max(1, Math.ceil(visibleBadges.length / this.props.config.badges.perPage));
		return (
			<>
				{this.props.badges.loaded && this.props.badges.error.text.length ? (
					<Box margin={{ bottom: "m" }}>
						<Alert type="error" header="Error loading data:">
							<Box variant="p">{this.props.badges.error.text}</Box>
							<hr />
							<Box variant="p">request error code: {this.props.badges.error.code} | request timestamp: {this.props.badges.error.timestamp}</Box>
						</Alert>
					</Box>
				) : null}
				<Table
					visibleColumns={this.getVisibleColumns()}
					stickyHeader
					loading={!this.props.badges.loaded}
					sortingColumn={{ sortingField: this.props.badges.sorting.sortingColumn.sortingField }}
					sortingDescending={this.props.badges.sorting.isDescending || this.props.badges.sorting.sortingDescending}
					onSortingChange={(sorting) => {
						const sortedData = cloneDeep(visibleBadges).sort((a, b) => this.compareBy(sorting.detail, a, b));
						this.props.setBadges({
							...this.props.badges,
							sorting: sorting.detail,
							data: sortedData,
						});
					}}
					columnDefinitions={[
						{
							id: "id",
							header: "Id",
							sortingField: "id",
							cell: (item) => (
								<Button
									onClick={() => {
										this.props.setBadgesEditModal({
											...this.props.badgesEditModal,
											...item,
										status: normalizeStatusOption(item.status),
											show: true,
											loaded: true,
										});
									}}>
									{item.id}
								</Button>
							),
						},
						{ id: "name", header: "Name", sortingField: "name", cell: (item) => item.name },
						{ id: "text_color", header: "Text color", sortingField: "text_color", cell: (item) => item.text_color },
						{ id: "background_color", header: "Background color", sortingField: "background_color", cell: (item) => item.background_color },
						{ id: "status", header: "Status", sortingField: "status", cell: (item) => item.status },
						{ id: "date", header: "Date", sortingField: "date", cell: (item) => this.formatBadgeDate(item) },
					]}
					items={sortedData.slice((this.props.badges.pagination.current - 1) * this.props.config.badges.perPage, this.props.badges.pagination.current * this.props.config.badges.perPage)}
					pagination={
						<Pagination
							disabled={!this.props.badges.loaded}
							currentPageIndex={this.props.badges.pagination.current}
							pagesCount={pagesCount}
							onChange={(page) =>
								this.props.setBadges({
									...this.props.badges,
									pagination: { ...this.props.badges.pagination, current: page.detail.currentPageIndex },
								})
							}
						/>
					}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{ title: "Badges per page", options: this.props.config.badges.preferences.perPage }}
							preferences={{ pageSize: this.props.config.badges.perPage }}
							title="Preferences"
							onConfirm={(preferences) => {
								localStorage.setItem("badges.perPage", preferences.detail.pageSize);
								this.props.setConfig({ ...this.props.config, badges: { ...this.props.config.badges, perPage: preferences.detail.pageSize } });
								this.props.setBadges({ ...this.props.badges, pagination: { current: 1, total: Math.ceil(visibleBadges.length / preferences.detail.pageSize) } });
							}}
						/>
					}
					header={
						<Box margin={{ bottom: "m" }}>
							<Header
								counter={<>({visibleBadges.length})</>}
								actions={
									<SpaceBetween direction="horizontal" size="l">
										<Button
											disabled={!this.props.badges.loaded}
											onClick={() => {
												this.props.setBadgesAddModal({
													show: true,
													loaded: true,
													id: 0,
													name: "",
													text_color: "#FFFFFF",
													background_color: "#FF4D4F",
													currentOption: {
														status: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }],
													},
													status: { value: "active", label: "Active" },
													date: new Date(),
												});
											}}
											variant="primary">
											Add badge
										</Button>
									</SpaceBetween>
								}>
								Badges
							</Header>
						</Box>
					}
					empty="No badges found"
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const user = useRecoilValue(userStore);
		const badges = useRecoilValue(badgesStore);
		const setBadges = useSetRecoilState(badgesStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const badgesAddModal = useRecoilValue(badgesAddModalStore);
		const setBadgesAddModal = useSetRecoilState(badgesAddModalStore);
		const badgesEditModal = useRecoilValue(badgesEditModalStore);
		const setBadgesEditModal = useSetRecoilState(badgesEditModalStore);
		return <Component {...props} user={user} badges={badges} setBadges={setBadges} config={config} setConfig={setConfig} badgesAddModal={badgesAddModal} setBadgesAddModal={setBadgesAddModal} badgesEditModal={badgesEditModal} setBadgesEditModal={setBadgesEditModal} />;
	};
}

export default withRecoil(Badges);
